import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faPercent,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";

const DecayTriggerConfigManagement = ({
  embedded = false,
  fallbackMode = false,
}) => {
  const [configs, setConfigs] = useState([]);
  const [corrosionCategories, setCorrosionCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    corrosionCategoryId: "",
    decayPercentageThreshold: "",
    description: "",
    isActive: true,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    fetchConfigs();
    fetchCorrosionCategories();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get("/decay-trigger-config");
      if (response.data.success) {
        setConfigs(response.data.data);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to fetch decay trigger configurations",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCorrosionCategories = async () => {
    try {
      const response = await api.get("/corrosion-categories?isActive=true");
      if (response.data.success) {
        setCorrosionCategories(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching corrosion categories:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConfig) {
        await api.put(`/decay-trigger-config/${editingConfig.id}`, formData);
      } else {
        await api.post("/decay-trigger-config", formData);
      }
      await fetchConfigs();
      setShowModal(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save configuration");
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      corrosionCategoryId: config.corrosionCategoryId,
      decayPercentageThreshold: config.decayPercentageThreshold,
      description: config.description || "",
      isActive: config.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (config) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this configuration for ${config.corrosionCategory?.categoryCode}?`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/decay-trigger-config/${config.id}`);
      await fetchConfigs();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete configuration");
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await api.put(`/decay-trigger-config/${config.id}`, {
        isActive: !config.isActive,
      });
      await fetchConfigs();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      corrosionCategoryId: "",
      decayPercentageThreshold: "",
      description: "",
      isActive: true,
    });
    setEditingConfig(null);
  };

  const filteredConfigs = configs.filter((config) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      config.corrosionCategory?.categoryCode
        ?.toLowerCase()
        .includes(searchLower) ||
      config.corrosionCategory?.categoryName
        ?.toLowerCase()
        .includes(searchLower) ||
      config.decayPercentageThreshold?.toString().includes(searchTerm)
    );
  });
  const totalFiltered = filteredConfigs.length;
  const totalPages = Math.ceil(totalFiltered / pageSize) || 1;
  const paginatedConfigs = filteredConfigs.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  if (loading) {
    return <div className={embedded ? "" : "p-6"}>Loading...</div>;
  }

  return (
    <div className={embedded ? "" : "p-6"}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Decay Trigger Configuration
          </h1>
          <p className="text-gray-600">
            Configure decay percentage thresholds for triggering repairs by
            corrosion category
          </p>
        </div>
      )}

      {fallbackMode && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This configuration is used as fallback only when no matching row
          exists in Simulation Triggers.
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by category code or threshold..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
        >
          <FontAwesomeIcon icon={faPlus} className="-ml-0.5 mr-1.5 h-5 w-5" />
          Add Configuration
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Corrosion Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Decay Threshold (%)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredConfigs.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No configurations found
                </td>
              </tr>
            ) : (
              paginatedConfigs.map((config) => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faShieldAlt}
                        className="h-5 w-5 text-blue-500 mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {config.corrosionCategory?.categoryCode}
                        </div>
                        <div className="text-sm text-gray-500">
                          {config.corrosionCategory?.categoryName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faPercent}
                        className="h-4 w-4 text-gray-400 mr-2"
                      />
                      <span className="text-sm text-gray-900">
                        {config.decayPercentageThreshold}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {config.description || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={config.isActive}
                      onClick={() => handleToggleActive(config)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        config.isActive ? "bg-blue-600" : "bg-gray-300"
                      }`}
                      title={config.isActive ? "Set inactive" : "Set active"}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          config.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(config)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <button
                      onClick={() => handleDelete(config)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalFiltered > pageSize && (
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 bg-white px-4 py-3 rounded-b-lg">
          <p className="text-sm text-gray-700">
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, totalFiltered)} of {totalFiltered}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingConfig ? "Edit" : "Add"} Decay Trigger Configuration
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corrosion Category *
                  </label>
                  <select
                    name="corrosionCategoryId"
                    value={formData.corrosionCategoryId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Category</option>
                    {corrosionCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.categoryCode} - {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Decay Percentage Threshold (%) *
                  </label>
                  <input
                    type="number"
                    name="decayPercentageThreshold"
                    value={formData.decayPercentageThreshold}
                    onChange={handleInputChange}
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 2.0 for C2, 5.0 for C5"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Repair will trigger when decay percentage reaches this
                    threshold
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional description"
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
                  >
                    {editingConfig ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DecayTriggerConfigManagement;
